class ServerSideLoginScreenController {
  constructor(
      $scope,
      $element,
      $compile,
      $window,
      $timeout,
      LogService,
      AppVersionService,
      API_URL,
      APIService,
      DataSyncService
  ) {
    "ngInject";
    this.$scope = $scope;
    this.$element = $element;
    this.$compile = $compile;
    this.$window = $window;
    this.$timeout = $timeout;
    this.LogService = LogService;
    this.AppVersionService = AppVersionService;
    this.APIService = APIService;
    this.API_URL = API_URL;
    this.DataSyncService = DataSyncService;

    this._errors = [];
    this._loading = false;
  }

  completeLoginWithToken(token) {
    return this.DataSyncService.loginAndSyncWithToken(token)
      .then(staffUser => {
        // Login was successful!
        // Call our onLogin callback
        const callbackParams = {staffUser};
        this.onLogin(callbackParams);
      })
      .catch(err => {
        // Login was not successful!
        // Let's show an error message
        this._addError(err);
      })
      .finally(() => {
        // Regardless, mark loading as finished
        this._loading = false;
      });
  }

  get errors() {
    return this._errors;
  }

  set onLogin(onLogin) {
    this._onLogin = onLogin;
  }

  get onLogin() {
    return this._onLogin;
  }

  get loading() {
    return this._loading;
  }

  set username(username) {
    this._username = username;
  }

  get username() {
    return this._username;
  }

  get loginButtonDisabled() {
    return !this.APIService.isDeviceOnline;
  }

  get forgotPasswordButtonDisabled() {
    return !this.APIService.isDeviceOnline;
  }

  get forgotPasswordURL() {
    if (!this._forgotPasswordUrl) {
      const baseUrl = this.API_URL.replace(/\/api\/[^\/]+/, '');
      this._forgotPasswordUrl = `${baseUrl}/auth/staff_request_password_reset.php`;
    }
    return this._forgotPasswordUrl;
  }

  /**
   * Login flow happens differently depending on whether we're in a native app build or a browser-based build
   *
   * Native app:
   * 1. User inputs username
   * 2. Use API to create new SAML login request and retrieve metadata including SAML request ID and URL
   * 2b. Include parameter browser_request=false so that the callback URL knows to instruct the user to close their window
   * 3. Open specified SAML URL in system browser
   * 4. User completes login in system browser and is instructed to close browser window
   * 5. We begin polling the API, attempting to exchange our SAML request ID for an authentication token
   * 6a. Upon successful exchange, we store the JWT auth token and complete the login process
   * 6b. Upon an error, we display an error message to the user
   *
   * Web app:
   * 1. User inputs username
   * 2. Use API to create new SAML login request and retrieve metadata including SAML request ID and URL
   * 2b. Include the parameter browser_request=true so that the callback URL knows to redirect to the staff app
   * 3. Navigate to specified SAML URL in the current browser window (unload the app)
   * 4. User completes login and is redirected to a callback page on our web server
   * 5. User is redirected to the staff app, with the SAML request ID included in a URL parameter
   * 6. Staff app is loaded and exchanges the SAML request ID from the URL for a JWT auth token
   */
  login() {
    this._clearErrors();

    // Ensure offline mode is not set (MJOL-450)
    if (this.APIService.isOfflineMode) {
      this.APIService.isOfflineMode = false;
    }

    const isBrowserApp = !(this.AppVersionService.isCordova);

    if (!this.username) {
      this._addError('Username is required');
      return;
    }

    this._loading = true;
    const samlLoginRequestData = {
      username: this.username,
      browser_request: isBrowserApp,
    };
    const samlRequestPromise = this.APIService.post('/auth/saml-request', samlLoginRequestData)
      .then(resp => {
        return resp.data;
      })
      .catch(err => {
        console.error("Got login error:", err);
      });

    if (isBrowserApp) {
      this.LogService.info("Launching in-browser login");
      return samlRequestPromise
        .then(responseData => {
          this.navigateToURL(responseData.url_with_redirect);
        });
    } else {
      this.LogService.info("Launching app login (new window)");
      return samlRequestPromise
        .then(responseData => {
          this.openURLInBrowser(responseData.url_with_redirect);
          return this.startPollingForLoginSuccess(responseData);
        });
    }
  }

  navigateToURL(url) {
    this.LogService.log("Navigating to URL:", url);
    this.$window.location.href = url;
  }

  openURLInBrowser(url) {
    this.LogService.log("Navigating to URL in system browseR:", url);
    this.$window.open(url, '_system');
  }

  startPollingForLoginSuccess(samlResponseData) {
    const requestId = samlResponseData.saml_request.request_id;
    const statusPath = `/auth/saml-request/${escape(requestId)}`;
    this.LogService.log("Start polling for login success...");

    // Poll every 5s
    const pollDelay = 5000;

    // Time out after 120s
    const pollTimeout = 120000;

    const startTime = Date.now();

    const checkStatus = () => {
      const path = `${statusPath}?nonce=${Date.now()}`;
      this.LogService.info("checkStatus() hitting URL:", path);
      return this.APIService.get(path, {force: true})
        .catch(err => {
          this.LogService.error("checkStatus() got error:", err);
          return this.$q.reject(err);
        })
        .then(resp => {
          const data = resp.data;
          const time = Date.now();
          const elapsed = time - startTime;

          this.LogService.info(`SAML Request ID ${requestId} got status:`, data);

          if (data.success) {
            // Hooray!
            return completeLogin();
          } else if (data.expired) {
            this._addError("Login expired; try again");
            this._loading = false;
            return false;
          } else if (!data.valid) {
            this._addError("Login failed; try again");
            this._loading = false;
            return false;
          } else if (elapsed >= pollTimeout) {
            this._addError("Login timed out; try again");
            this._loading = false;
          } else {
            // Try again!
            this.LogService.info(`SAML check inconclusive; waiting another ${pollDelay} ms to try again`);
            return this.$timeout(checkStatus, pollDelay);
          }
        });
    }

    const completeLogin = () => {
      return this.DataSyncService.loginAndSyncWithSamlRequestId(requestId)
        .then(staffUser => {
          // Login was successful!
          // Call our onLogin callback
          let callbackParams = {staffUser};
          this.onLogin(callbackParams);
        })
        .catch(err => {
          this._addError(err);
        })
    }

    return this.$timeout(checkStatus, pollDelay)
      .finally(() => {
        this._loading = false;
      })
  }

  /**
   * Private methods
   */

  _clearErrors() {
    this._errors.length = 0;
  }

  _addError(error) {
    this.errors.push(error);
  }
}

export default ServerSideLoginScreenController;
